import { uiText, useUiLanguage } from '../../../utils/uiTranslation';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getMemberInitials, summarizeDepartmentAccess } from '../../../utils/teamAccess';

const TeamMemberCard = ({
  actionKey,
  item,
  language,
  canEditMember = () => false,
  canRemoveMember = () => false,
  getActionStatusLabel = () => '',
  onEdit,
  onRemove,
  permissionCount,
  permissionLabels,
  type = 'active'
}) => {
  const { t } = useTranslation('profile');
  const isInvite = type === 'invite';
  const removeKey = `${isInvite ? 'remove-invite' : 'remove-member'}:${item.key || item.uid}`;
  const isBusy = actionKey === removeKey;
  const displayName = item.name || item.email || 'Unnamed team member';
  const displayEmail = item.email || t('team.emailNotAvailable', 'Email not available');
  const showEditAction = !isInvite && canEditMember(item);
  const showRemoveAction = isInvite || canRemoveMember(item);
  const actionStatusLabel = !isInvite ? getActionStatusLabel(item) : '';

  return (
    <article className={`profile-member-card ${isInvite ? 'pending' : ''}`}>
      <div className="profile-member-card__top">
        <div className="profile-directory-col profile-directory-col--member">
          <div className={`profile-directory-avatar ${isInvite ? 'pending' : ''}`}>
            {isInvite ? (
              <i className="fa-regular fa-envelope"></i>
            ) : item.photoUrl ? (
              <img src={item.photoUrl} alt={displayName} />
            ) : (
              <span>{getMemberInitials(item)}</span>
            )}
          </div>
          <div className="profile-directory-copy">
            <strong>{displayName}</strong>
            <span>{item.position || t('team.positionNotSet', 'Position not set')}</span>
            <p>{displayEmail}</p>
          </div>
        </div>

        <span className={`profile-status-pill ${isInvite ? 'pending' : 'active'}`}>
          {isInvite ? t('team.statusInvited', 'Invited') : t('team.statusActive', 'Active')}
        </span>
      </div>

      <div className="profile-member-keyline">
        <div className="profile-member-keyline__item">
          <label>{t('team.departmentScope', 'Department scope')}</label>
          <span>{summarizeDepartmentAccess(item, language)}</span>
        </div>
        <div className="profile-member-keyline__item">
          <label>{t('team.featureAccess', 'Feature access')}</label>
          <span>{t('team.enabledPermissions', '{{count}} enabled permissions', { count: permissionCount })}</span>
        </div>
      </div>

      <div className="profile-mini-chip-row">
        {permissionLabels.length > 0 ? (
          permissionLabels.map((label) => (
            <span key={label} className="profile-mini-chip">
              {uiText(label)}
            </span>
          ))
        ) : (
          <span className="profile-mini-chip">{t('team.noEnabledPermissions', 'No enabled permissions')}</span>
        )}
      </div>

      <div className="profile-directory-col profile-directory-col--actions">
        {item.sourceType === 'owner' ? (
          <span className="profile-status-pill active" style={{ alignSelf: 'center' }}>
            {t('team.workspaceOwner', 'Workspace owner')}
          </span>
        ) : (
          <>
            {showEditAction && (
              <button type="button" className="profile-btn-ghost" onClick={onEdit}>
                {t('team.manageAccess', 'Manage access')}
              </button>
            )}
            {showRemoveAction ? (
              <button type="button" className="profile-btn-danger" onClick={onRemove} disabled={isBusy}>
                {isBusy
                  ? t('team.working', 'Working...')
                  : isInvite
                  ? t('team.cancelInvite', 'Cancel invite')
                  : t('team.remove', 'Remove')}
              </button>
            ) : actionStatusLabel ? (
              <span className="profile-status-pill active" style={{ alignSelf: 'center' }}>
                {actionStatusLabel}
              </span>
            ) : null}
          </>
        )}
      </div>
    </article>
  );
};

const SectionBlock = ({ children, count, title }) => (
  <div className="profile-team-group">
    <h3 className="profile-team-group__title">
      {title}
      <span className="profile-team-group__count">({count})</span>
    </h3>
    {children}
  </div>
);

const TeamAccessSection = ({
  filteredPendingInvites,
  filteredTeamMembers,
  canEditTeamMember,
  canRemoveTeamMember,
  getTeamMemberActionStatus,
  getEnabledPermissionCount,
  inviteMember,
  language,
  openPermissionsEditor,
  removePendingInvite,
  removeTeamMember,
  searchTerm,
  seatUsageCount,
  setSearchTerm,
  setTeamFilter,
  subscriptionData,
  teamActionKey,
  teamFilter,
  topPermissionLabels
}) => {
  useUiLanguage();
  const { t } = useTranslation('profile');

  const filters = [
    { key: 'all', label: t('team.filterAll', 'All members') },
    { key: 'active', label: t('team.filterActive', 'Active') },
    { key: 'invited', label: t('team.filterInvited', 'Invited') },
    { key: 'restricted', label: t('team.filterScoped', 'Scoped') },
    { key: 'approvers', label: t('team.filterApprovers', 'Approvers') }
  ];

  const seatsRemaining = subscriptionData.seatLimit === 'unlimited'
    ? t('team.unlimitedCapacity', 'Unlimited plan capacity')
    : subscriptionData.seatLimit
      ? t('team.seatsRemaining', '{{count}} seats remaining', { count: Math.max(subscriptionData.seatLimit - seatUsageCount, 0) })
      : t('team.capacityUnavailable', 'Plan capacity not available');

  return (
    <div className="profile-section-stack">
      <section className="profile-panel profile-panel-main">
        <div className="profile-panel-header profile-panel-header--stack-sm">
          <div>
            <h2>{t('team.teamAccessTitle', 'Team access')}</h2>
            <p>{t('team.seatsInUse', '{{count}} seats in use.', { count: seatUsageCount })} {seatsRemaining}</p>
          </div>
          <button type="button" className="profile-btn-primary" onClick={inviteMember}>
            {t('team.inviteTeamMember', 'Invite team member')}
          </button>
        </div>

        <div className="profile-team-toolbar">
          <div className="profile-search">
            <i className="fa-solid fa-search"></i>
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={t('team.searchPlaceholder', 'Search by name, email, position, or department')}
            />
          </div>

          <div className="profile-filter-bar profile-filter-bar--square">
            {filters.map((filter) => (
              <button
                key={filter.key}
                type="button"
                className={`profile-filter-pill ${teamFilter === filter.key ? 'active' : ''}`}
                onClick={() => setTeamFilter(filter.key)}
              >
                {uiText(filter.label)}
              </button>
            ))}
          </div>
        </div>

        {filteredTeamMembers.length === 0 && filteredPendingInvites.length === 0 ? (
          <div className="profile-empty-state">
            <strong>{t('team.noResults', 'No team members match the current view.')}</strong>
            <p>{t('team.noResultsHint', 'Try a different filter or invite a new team member.')}</p>
          </div>
        ) : (
          <div className="profile-team-sections">
            <SectionBlock count={filteredTeamMembers.length} title={t('team.activeSectionTitle', 'Active team')}>
              <div className="profile-member-grid">
                {filteredTeamMembers.map((member) => (
                  <TeamMemberCard
                    key={member.uid}
                    actionKey={teamActionKey}
                    canEditMember={canEditTeamMember}
                    canRemoveMember={canRemoveTeamMember}
                    getActionStatusLabel={getTeamMemberActionStatus}
                    item={member}
                    language={language}
                    onEdit={() => openPermissionsEditor(member)}
                    onRemove={() => removeTeamMember(member)}
                    permissionCount={getEnabledPermissionCount(member.permissions)}
                    permissionLabels={topPermissionLabels(member)}
                  />
                ))}
              </div>
            </SectionBlock>

            {filteredPendingInvites.length > 0 && (
              <SectionBlock count={filteredPendingInvites.length} title={t('team.pendingSectionTitle', 'Pending invites')}>
                <div className="profile-member-grid">
                  {filteredPendingInvites.map((invite) => (
                    <TeamMemberCard
                      key={invite.key}
                      actionKey={teamActionKey}
                      canEditMember={() => false}
                      canRemoveMember={() => true}
                      getActionStatusLabel={() => ''}
                      item={invite}
                      language={language}
                      onRemove={() => removePendingInvite(invite)}
                      permissionCount={getEnabledPermissionCount(invite.permissions)}
                      permissionLabels={topPermissionLabels(invite)}
                      type="invite"
                    />
                  ))}
                </div>
              </SectionBlock>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default TeamAccessSection;
